import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useUserStore } from 'store'
import { AnalyticsViewDTO, AnalyticsViews } from 'services/user'
import {
	DEFAULT_LAYOUT,
	ReportLayout,
	SavedView,
	useReportLayoutStore
} from '../store/use-report-layout-store'

// Loads the views from the backend into the config store on mount, and binds the
// account/asset/user so the store's view mutations can persist (DEV-2377, §2).
// All three buckets come from the backend: ClickGuard's Default views (seeded by
// migration-132, read-only, the same for everyone — §2.2), the user's private
// views and the workspace's shared views.

// Never a real user id, so `isViewOwner` is false for defaults and nobody can
// rename / delete / re-scope them from the UI (the backend forbids it too).
const SYSTEM_AUTHOR = '__system__'

const toSavedView = (dto: AnalyticsViewDTO): SavedView => ({
	id: dto.id,
	label: dto.name,
	scope: dto.scope === 'default' ? 'default' : dto.scope === 'shared' ? 'shared' : 'yours',
	// author = owner id; the store compares it to the signed-in user for ownership.
	author: dto.owner_user_id ?? SYSTEM_AUTHOR,
	authorName: dto.owner_name ?? undefined,
	sortOrder: dto.sort_order ?? undefined,
	layout:
		dto.config && Object.keys(dto.config).length
			? (dto.config as unknown as ReportLayout)
			: DEFAULT_LAYOUT
})

export const useViewsSync = () => {
	const { accountId, assetId } = useParams<{ accountId: string; assetId: string }>()
	// ?view=<id> (the Scheduled Reports page's "Manage" deep link) names the view
	// the visitor came for. Captured ONCE at mount straight off the address bar —
	// not a dep, and not the live router params — so neither a later URL rewrite
	// (date picker, breadcrumb scope) nor a re-render can lose it before the
	// backend answers.
	const linkedViewId = useRef(new URLSearchParams(window.location.search).get('view')).current
	const userId = useUserStore((s) => s.user?.id)
	const configureViews = useReportLayoutStore((s) => s.configureViews)
	const hydrateViews = useReportLayoutStore((s) => s.hydrateViews)
	const loadInitialView = useReportLayoutStore((s) => s.loadInitialView)

	useEffect(() => {
		if (!accountId || !assetId || !userId) return
		configureViews(accountId, assetId, userId)
		let cancelled = false
		AnalyticsViews.list(accountId, assetId)
			.then((res) => {
				if (cancelled) return
				// The backend returns defaults in menu order (sort_order); the first is
				// the fallback default that opens for a user without a main view (§2.10).
				const defaults = res.default.map(toSavedView)
				const views = [...defaults, ...res.private.map(toSavedView), ...res.shared.map(toSavedView)]
				const fallbackId = defaults[0]?.id ?? null
				hydrateViews(views, res.defaultViewId, fallbackId)
				// The user's main view opens by default (§2.10) — not whatever was last
				// on screen, which the persisted store would otherwise carry across
				// sessions and even across workspaces. A main view that no longer
				// exists falls back to the fallback Default view.
				//
				// EXCEPT when the URL names a view: "Manage" on the Scheduled Reports
				// page arrives with ?view=<id>&drawer=schedule. The header applies that
				// view straight away off the persisted view list, and this backend load
				// used to land afterwards and yank the page back to the main view — so
				// the deep-linked view wins here too.
				const exists = (id: string | null) => !!id && views.some((v) => v.id === id)
				loadInitialView(
					exists(linkedViewId) ? linkedViewId : exists(res.defaultViewId) ? res.defaultViewId : null
				)
			})
			.catch(() => undefined)
		return () => {
			cancelled = true
		}
		// linkedViewId is intentionally not a dep (see above).
		// eslint-disable-next-line
	}, [accountId, assetId, userId, configureViews, hydrateViews, loadInitialView])
}
